import React, { useContext, useMemo } from "react";
import { useTracking } from "@local-logic/core/tracking";

import useEffectOnce from "~/hooks/useEffectOnce";
import useIsMobile from "~/hooks/useIsMobile";
import { useStateManagerContext } from "~/context/stateManager";
import { useGeoLocationInfo } from "~/hooks/useGeoLocationInfo";
import { useGeographies } from "~/features/clientLibrary/useGeographies";
import { useProfiles } from "~/features/clientLibrary/useProfiles";

import { type LocationInfo } from "~/types/profiles";
import DesktopFallback from "./assets/images/desktop_fallback.jpg";
import MobileFallback from "./assets/images/mobile_fallback.jpg";

import {
  type NeighborhoodHeroProps,
  TrackingPipelineEvents,
  AnalyticsEvents,
} from "./types";

interface Location {
  label?: string;
  lat: number;
  lng: number;
}

export type DefaultContext = {
  isLoading: boolean;
  locationInfo?: LocationInfo;
  neighborhoodImage: string | null;
  isFallbackImage: boolean;
  neighborhoodSummary?: string;
};

const NeighborhoodHeroContext = React.createContext<DefaultContext>(
  {} as DefaultContext
);

const useNeighborhoodHero = () => useContext(NeighborhoodHeroContext);

const NeighborhoodHeroProvider = ({
  children,
}: {
  children: React.JSX.Element;
}) => {
  const { sdkState } = useStateManagerContext<NeighborhoodHeroProps>();
  const locale = sdkState.globalOptions?.locale || "en";
  const tracking = useTracking();
  const isMobile = useIsMobile();

  useEffectOnce(() => {
    tracking.postEvent({
      eventType: TrackingPipelineEvents.SDK_VIEWED,
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.SDK_VIEWED,
    });
  });

  const { isLoading: isGeoLocationInfoLoading, locationInfo } =
    useGeoLocationInfo({
      lat: sdkState.options?.lat,
      lng: sdkState.options?.lng,
      geographyId: sdkState.options?.geographyId,
      externalId: sdkState?.globalOptions?.externalId,
      locale,
    });

  const { data: geographyData } = useGeographies({
    geogID: locationInfo?.geogID,
    externalId: sdkState?.globalOptions?.externalId,
  });

  const { data: profilesData, isFetching: isProfilesFetching } = useProfiles({
    location: {
      lat: locationInfo?.coordinates?.lat,
      lng: locationInfo?.coordinates?.lng,
    },
    geogID: locationInfo?.geogID,
    externalId: sdkState?.globalOptions?.externalId,
  });

  const geography = useMemo(() => {
    if (typeof locationInfo?.geogID === "string") {
      return geographyData?.[locationInfo.geogID];
    }

    return undefined;
  }, [geographyData]);

  const isLoading = useMemo(
    () => isGeoLocationInfoLoading || isProfilesFetching,
    [isGeoLocationInfoLoading, isProfilesFetching]
  );

  const neighborhoodImage = useMemo(() => {
    const lowercaseOptionsImageValue = (
      sdkState.options?.image || ""
    ).toLocaleLowerCase();

    // Always hide the image if the option is set to "hide"
    if (lowercaseOptionsImageValue === "hide") {
      return null;
    }

    // If the geography has an image, use it by default
    if (
      typeof geography?.image_url === "string" &&
      geography?.image_url.length > 0
    ) {
      return geography.image_url as string;
    }

    // If the image option is set to a string, use it as the image (unless the string is "noFallback" which is obviuosly not an image)
    if (
      typeof sdkState.options?.image === "string" &&
      lowercaseOptionsImageValue !== "nofallback" &&
      lowercaseOptionsImageValue.substring(0, 4) === "http"
    ) {
      return sdkState.options.image;
    }

    // No fallback image
    if (lowercaseOptionsImageValue === "nofallback") {
      return null;
    }

    // Fallback image based on the device
    return isMobile ? MobileFallback : DesktopFallback;
  }, [geography?.image_url, sdkState?.options?.image, isMobile]);

  const contextValue = useMemo(
    () => ({
      isLoading,
      locationInfo: {
        ...locationInfo,
        microLocation: geography?.name?.[locale] || locationInfo?.microLocation,
      } as LocationInfo,
      geographyData,
      isFallbackImage:
        neighborhoodImage !== null &&
        [MobileFallback, DesktopFallback].includes(neighborhoodImage),
      neighborhoodImage,
      neighborhoodSummary: profilesData?.profiles.summary,
    }),
    [
      isLoading,
      locationInfo,
      neighborhoodImage,
      profilesData?.profiles?.summary,
      sdkState,
    ]
  );

  return (
    <NeighborhoodHeroContext.Provider value={contextValue}>
      {children}
    </NeighborhoodHeroContext.Provider>
  );
};

export { useNeighborhoodHero, NeighborhoodHeroProvider, type Location };
